import React from 'react';
import type { ReactionWeightsFormState } from '../types/reactionWeights';
import {
  REACTION_WEIGHT_MAX,
  REACTION_WEIGHT_MIN,
  REACTION_WEIGHT_STEP,
  commitReactionWeightDraft,
  sanitizeReactionWeightDraft,
} from '../types/reactionWeights';
import './FineTuningReactionWeightsFields.css';

export interface FineTuningReactionWeightsFieldsProps {
  value: ReactionWeightsFormState;
  onChange: (next: ReactionWeightsFormState) => void;
  disabled?: boolean;
  idPrefix?: string;
  fieldErrors?: Partial<Record<WeightFieldKey, string>>;
}

type WeightFieldKey = keyof ReactionWeightsFormState;

const FIELDS: Array<{
  key: WeightFieldKey;
  label: string;
  hint?: string;
  group: 'admin' | 'user';
}> = [
  { key: 'admin_like', label: 'Admin like weight', group: 'admin' },
  { key: 'admin_dislike', label: 'Admin dislike weight', group: 'admin' },
  {
    key: 'admin_propagation',
    label: 'Admin propagation to internal agents',
    hint: 'Share of the coordinator reaction applied to internal agents.',
    group: 'admin',
  },
  { key: 'user_like', label: 'User like weight', group: 'user' },
  { key: 'user_dislike', label: 'User dislike weight', group: 'user' },
  {
    key: 'user_propagation',
    label: 'User propagation to internal agents',
    hint: 'Share of the coordinator reaction applied to internal agents.',
    group: 'user',
  },
];

function updateField(
  value: ReactionWeightsFormState,
  key: WeightFieldKey,
  nextValue: string
): ReactionWeightsFormState {
  return { ...value, [key]: nextValue };
}

const FineTuningReactionWeightsFields: React.FC<FineTuningReactionWeightsFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  idPrefix = 'ft-reaction-weights',
  fieldErrors,
}) => {
  const renderGroup = (group: 'admin' | 'user', title: string) => (
    <div className="ft-reaction-weights__group">
      <h5 className="ft-reaction-weights__group-title">{title}</h5>
      <div className="ft-reaction-weights__fields">
        {FIELDS.filter((field) => field.group === group).map((field) => {
          const inputId = `${idPrefix}-${field.key}`;
          const fieldError = fieldErrors?.[field.key];
          return (
            <div
              className={`ft-reaction-weights__field${fieldError ? ' ft-reaction-weights__field--invalid' : ''}`}
              key={field.key}
            >
              <label className="ft-reaction-weights__label" htmlFor={inputId}>
                {field.label}
              </label>
              <input
                id={inputId}
                type="number"
                className={`ft-reaction-weights__input${fieldError ? ' ft-reaction-weights__input--invalid' : ''}`}
                min={REACTION_WEIGHT_MIN}
                max={REACTION_WEIGHT_MAX}
                step={REACTION_WEIGHT_STEP}
                autoComplete="off"
                placeholder="Default (0–1)"
                value={value[field.key]}
                disabled={disabled}
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={fieldError ? `${inputId}-error` : undefined}
                onChange={(e) =>
                  onChange(updateField(value, field.key, sanitizeReactionWeightDraft(e.target.value)))
                }
                onBlur={(e) =>
                  onChange(updateField(value, field.key, commitReactionWeightDraft(e.target.value)))
                }
              />
              {fieldError ? (
                <p className="ft-reaction-weights__field-error" id={`${inputId}-error`} role="alert">
                  {fieldError}
                </p>
              ) : field.hint ? (
                <p className="ft-reaction-weights__field-hint">{field.hint}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <section className="ft-reaction-weights" aria-labelledby={`${idPrefix}-title`}>
      <h4 className="ft-reaction-weights__title" id={`${idPrefix}-title`}>
        Fine tuning reaction weights
      </h4>
      <p className="ft-reaction-weights__intro">
        Fine tuning only. Weights control how often each reaction is included when the dataset is
        built: 1 = always, 0 = never, values in between = random sampling (for example, 0.3 ≈ 30%
        chance). Leave a field empty to use the backend default. Comments and ground truth are not
        weighted.
      </p>
      <div className="ft-reaction-weights__groups">
        {renderGroup('admin', 'Admin reactions')}
        {renderGroup('user', 'User reactions')}
      </div>
    </section>
  );
};

export default FineTuningReactionWeightsFields;
